import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import BackButton from "../components/BackButton";
import BackToTop from "../components/BackToTop";

export default function TokenAndRails() {
    const nameExt = " | charlie-gp";
    const [postTitle, postDate, readTime] = [
        "The Token and the Rails: A Tale of Authentication",
        "Feb. 1, 2026",
        "5min. read",
    ];

    return (
        <div id="posts">
            <Helmet>
                <title>{postTitle + nameExt}</title>
            </Helmet>
            <BackButton />

            <div id="post-date">
                <small>
                    {postDate} &nbsp;&bull;&nbsp; {readTime} &nbsp;&bull;&nbsp;{" "}
                    <Link to="/">all posts</Link>
                </small>
            </div>

            <h1 id="post-title">{postTitle}</h1>

            <p>
                Authentication is the cornerstone of any secure application. In a modern Rails backend serving mobile or frontend clients, we often move away from stateful sessions to stateless token-based authentication. This post explores how we verify requests using JSON Web Tokens (JWT) and integrate them seamlessly into the Rails request lifecycle.
            </p>

            <h2 className="post-sect">The Request</h2>
            <p>
                When a client (like a React Native app) sends a request to the API, it includes an <code>Authorization</code> header. This header usually carries a Bearer token, which acts as the user's digital ID card for that session.
            </p>
            <p>
                Unlike cookie-based sessions, the server doesn't need to store this state in memory. Instead, it relies on the cryptographic signature of the token to verify its validity.
            </p>

            <h2 className="post-sect">The Verification</h2>
            <p>
                We typically implement a <code>AuthenticateRequest</code> service or concern. Its job is simple but critical: extract the token, decode it, and verify the signature.
            </p>
            <p>
                A standard JWT consists of three parts:
            </p>
            <ul className="lead">
                <li><strong>Header:</strong> Algorithm and token type.</li>
                <li><strong>Payload:</strong> The data claims (e.g., <code>user_id</code>, expiration).</li>
                <li><strong>Signature:</strong> The verification hash ensuring the token hasn't been tampered with.</li>
            </ul>
            <p>
                Rails uses its system-wide <code>secret_key_base</code> to sign and verify these tokens. If the signature matches, we know the token was issued by us and hasn't been altered.
            </p>

            <h2 className="post-sect">The User Session</h2>
            <p>
                Once verified, we decode the payload to retrieve the <code>user_id</code>. We then fetch the user from the database and set a global context, often using <code>Current.user</code>.
            </p>
            <p>
                This allows controllers and background jobs to access the authenticated user seamlessly. If the token is invalid, expired, or missing, the request is halted immediately with a <code>401 Unauthorized</code> response, ensuring that no unverified data ever touches the protected parts of the application.
            </p>
            <p>
                This "Rails way" of handling tokens provides a robust, scalable, and secure foundation for any API-driven application.
            </p>

            <BackToTop />

            <div className="mt-5">
                <BackButton />
            </div>
        </div>
    );
}
